import { useState, useEffect, useCallback } from 'react'

// ─── useLiveCollapse — 3-step collapse state for the live region (per chat) ─────────
//
// The live zone (below the committed/live divider) can be shown at three fixed sizes so a
// long stream doesn't push the conversation around:
//   3 = full   (default — no height cap, runs long like before)
//   1 = min    (~1 line, fixed height, newest line pinned)
//   2 = medium (~5-6 lines, fixed height, newest pinned; older clipped at the top)
// Cycle order is 3 → 1 → 2 → 3. Persisted in localStorage per chat so it stays put across
// sends and reloads (no backend change). Shared by WhimerCleanView + the Term² view so both
// honour the same choice.

export type LiveCollapse = 1 | 2 | 3

const keyFor = (chatId: string) => `whimer:liveCollapse:${chatId}`

function read(chatId: string): LiveCollapse {
  try {
    const v = Number(localStorage.getItem(keyFor(chatId)))
    return v === 1 || v === 2 ? (v as LiveCollapse) : 3
  } catch {
    return 3
  }
}

export function useLiveCollapse(chatId: string): { state: LiveCollapse; cycle: () => void } {
  const [state, setState] = useState<LiveCollapse>(() => read(chatId))

  // Re-read when switching chats (the view is reset via effect, not remounted).
  useEffect(() => { setState(read(chatId)) }, [chatId])

  const cycle = useCallback(() => {
    setState(prev => {
      const next: LiveCollapse = prev === 3 ? 1 : prev === 1 ? 2 : 3
      try { localStorage.setItem(keyFor(chatId), String(next)) } catch { /* ignore */ }
      return next
    })
  }, [chatId])

  return { state, cycle }
}
